/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import { Card, Form, Row, Col, InputGroup, Button } from 'react-bootstrap';
import { formatFloatInput } from '../../util/util';

interface Props {
  draft: any;
  logic: Record<string, any>;
  setLogic: (v: any) => void;
}

const ScoringRulesEditor: React.FC<Props> = ({ draft, logic, setLogic }) => {
  const updateLogic = (update: Record<string, any>) => {
    setLogic((prev: any) => ({ ...prev, ...update }));
  };

  const addRange = () => {
    const newRanges = [...(logic.ranges || []), { min: 0, max: 100, score: 0 }];
    updateLogic({ ranges: newRanges });
  };

  const updateRange = (idx: number, field: string, val: number | string) => {
    const newRanges = [...(logic.ranges || [])];
    newRanges[idx] = { ...newRanges[idx], [field]: val };
    updateLogic({ ranges: newRanges });
  };

  const deleteRange = (idx: number) => {
    const newRanges = logic.ranges.filter((_: any, i: number) => i !== idx);
    updateLogic({ ranges: newRanges });
  };

  const type = draft.type;

  return (
    <Card body className="bg-light mb-3">
      <Card.Title>Scoring Rules</Card.Title>

      {/* Automatic scoring types */}
      {type === 'industry' && (
        <>
          <p className="fw-bold">Scoring for industry is set automatically.</p>
          <p>If you need manual scoring, change to single/multi choice.</p>
        </>
      )}
      {type === 'country' && (
        <>
          <p className="fw-bold">Scoring for country is set automatically.</p>
          <p>If you need manual scoring, change to single/multi choice.</p>
        </>
      )}

      {/* Text, multiline, file, date */}
      {(type === 'text' ||
        type === 'multiline' ||
        type === 'file' ||
        type === 'date') && (
        <>
          <Form.Label>Score for entered value</Form.Label>
          <Form.Control
            type="text"
            value={logic.score ?? 0}
            onChange={(e) => {
              const score = formatFloatInput(e.target.value);
              updateLogic({ ...logic, score });
            }}
          />
        </>
      )}

      {/* Boolean */}
      {type === 'boolean' && (
        <>
          <Form.Label>Score for YES answer</Form.Label>
          <Form.Control
            type="text"
            value={logic.score ?? 0}
            onChange={(e) => {
              const score = formatFloatInput(e.target.value);
              updateLogic({ ...logic, score });
            }}
          />
        </>
      )}

      {/* Number / Percentage */}
      {(type === 'number' || type === 'percentage') && (
        <div>
          <Form.Label>Numeric Ranges</Form.Label>
          {logic.ranges?.map((r: any, i: number) => (
            <Row key={i} className="mb-2 align-items-center">
              <Col>
                <InputGroup>
                  <InputGroup.Text>Min</InputGroup.Text>
                  <Form.Control
                    type="text"
                    value={r.min}
                    onChange={(e) => {
                      let min = formatFloatInput(e.target.value);
                      if (
                        type === 'percentage' &&
                        parseFloat(min.toString()) < 0
                      )
                        min = '0';
                      updateRange(i, 'min', min);
                    }}
                  />
                  <InputGroup.Text>Max</InputGroup.Text>
                  <Form.Control
                    type="text"
                    value={r.max}
                    onChange={(e) => {
                      let max = formatFloatInput(e.target.value);
                      if (
                        type === 'percentage' &&
                        parseFloat(max.toString()) > 100
                      )
                        max = '100';
                      updateRange(i, 'max', max);
                    }}
                  />
                  <InputGroup.Text>Score</InputGroup.Text>
                  <Form.Control
                    type="text"
                    value={r.score}
                    onChange={(e) => {
                      const score = formatFloatInput(e.target.value);
                      updateRange(i, 'score', score);
                    }}
                  />
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => deleteRange(i)}
                  >
                    ✕
                  </Button>
                </InputGroup>
              </Col>
            </Row>
          ))}
          <Row>
            <Col>
              <Button variant="outline-primary" size="sm" onClick={addRange}>
                + Add Range
              </Button>
            </Col>
          </Row>
        </div>
      )}
    </Card>
  );
};

export default ScoringRulesEditor;
